import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { FiTrendingUp, FiBarChart2, FiAward, FiTarget, FiAlertTriangle, FiClipboard, FiDollarSign, FiPackage, FiUsers, FiCalendar, FiPlay, FiLink2 } from 'react-icons/fi'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsMore from 'highcharts/highcharts-more'
import Funnel from 'highcharts/modules/funnel'
import Heatmap from 'highcharts/modules/heatmap'
import Treemap from 'highcharts/modules/treemap'
import { stripLeadingPlanBlock } from '../utils/planBlock'
import { getApiHost } from '../../../modules/apiHost'
import { toWidgetSpec, widgetSpecFromFencedCode } from 'modules/widgetSpec'
import WidgetPreviewCard from './WidgetPreviewCard'

// Register the Highcharts 11 module factories on the SAME v11 instance we render
// with (highcharts-react-official below). Mirrors the proven pattern in
// Reports/components/GroupedTrendsChart.tsx and sharedComponents/CustomHighmaps.
// Do NOT register these against react-highcharts' bundled Highcharts 6 — that
// version mismatch throws "Cannot read properties of undefined (reading
// 'Core/Utilities.js')" and blanks the whole AI page.
HighchartsMore(Highcharts)
Funnel(Highcharts)
Heatmap(Highcharts)
Treemap(Highcharts)

interface AgentResponseRendererProps {
  content: string
  onActionClick?: (actionNumber: number, actionTitle: string) => void
  /** Skip the outer card chrome (border/padding/shadow) when embedded in a
   *  container that already provides it (e.g. the agent AnswerCard). */
  bare?: boolean
}

// Chart-spec contract (DESIGN.md §1.7). These 11 `type` literals MUST stay in
// sync, byte-for-byte, with the papaya backend system prompt in
// `papaya/src/api/routes/openai/tools/agentTools.ts` under "## Rendering
// Charts". This renderer is the validation authority: if the prompt emits a
// `type` this union does not accept (or vice versa), charts silently fail. Do
// not add/rename a chart type here without updating that prompt in lockstep.
type ChartKind =
  | 'line'
  | 'bar'
  | 'stackedBar'
  | 'area'
  | 'pie'
  | 'donut'
  | 'scatter'
  | 'combo'
  | 'funnel'
  | 'heatmap'
  | 'treemap'

type CategoricalSpec = {
  type: 'line' | 'bar' | 'stackedBar' | 'area' | 'combo'
  title?: string
  labels: string[]
  datasets: Array<{ label?: string; data: number[]; kind?: 'column' | 'line'; axis?: 'left' | 'right' }>
  yAxisPrefix?: string
  yAxisSuffix?: string
  y2AxisPrefix?: string
  y2AxisSuffix?: string
}

type ShareSpec = {
  type: 'pie' | 'donut' | 'funnel' | 'treemap'
  title?: string
  data: Array<{ label: string; value: number; color?: string }>
}

type ScatterSpec = {
  type: 'scatter'
  title?: string
  xAxisLabel?: string
  yAxisLabel?: string
  series: Array<{ label?: string; points: Array<{ x: number; y: number; name?: string }> }>
}

type HeatmapSpec = {
  type: 'heatmap'
  title?: string
  xLabels: string[]
  yLabels: string[]
  points: Array<[number, number, number]>
  valuePrefix?: string
  valueSuffix?: string
}

type InlineChartSpec = CategoricalSpec | ShareSpec | ScatterSpec | HeatmapSpec

const INLINE_CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4']

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

const optString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)

const CATEGORICAL_TYPES = ['line', 'bar', 'stackedBar', 'area', 'combo'] as const
const SHARE_TYPES = ['pie', 'donut', 'funnel', 'treemap'] as const

const isCategoricalType = (t: unknown): t is CategoricalSpec['type'] =>
  typeof t === 'string' && (CATEGORICAL_TYPES as readonly string[]).includes(t)
const isShareType = (t: unknown): t is ShareSpec['type'] =>
  typeof t === 'string' && (SHARE_TYPES as readonly string[]).includes(t)

// Strict validation per chart family. Strict guards are critical: a non-chart
// JSON object (or a malformed chart) must return null so `wrapBareChartJson`
// keeps treating it as plain JSON/code and never renders a broken chart.
const toInlineChartSpec = (value: unknown): InlineChartSpec | null => {
  if (!value || typeof value !== 'object') return null
  const obj = value as Record<string, unknown>
  const type = obj.type

  // --- Categorical + combo: labels[] + datasets[] (strict data.length) ---
  if (isCategoricalType(type)) {
    const labels = obj.labels
    const datasets = obj.datasets
    if (
      !Array.isArray(labels) ||
      labels.length === 0 ||
      !labels.every((l) => typeof l === 'string') ||
      !Array.isArray(datasets) ||
      datasets.length === 0
    ) {
      return null
    }
    const normalizedDatasets = datasets
      .map((d) => {
        if (!d || typeof d !== 'object') return null
        const row = d as Record<string, unknown>
        if (!Array.isArray(row.data) || row.data.length !== labels.length || !row.data.every(isFiniteNumber)) {
          return null
        }
        const kind = row.kind === 'column' || row.kind === 'line' ? row.kind : undefined
        const axis = row.axis === 'left' || row.axis === 'right' ? row.axis : undefined
        return {
          label: typeof row.label === 'string' ? row.label : '',
          data: row.data as number[],
          kind,
          axis,
        }
      })
      .filter(Boolean) as CategoricalSpec['datasets']
    if (normalizedDatasets.length === 0) return null
    return {
      type,
      title: optString(obj.title),
      labels: labels as string[],
      datasets: normalizedDatasets,
      yAxisPrefix: optString(obj.yAxisPrefix),
      yAxisSuffix: optString(obj.yAxisSuffix),
      y2AxisPrefix: optString(obj.y2AxisPrefix),
      y2AxisSuffix: optString(obj.y2AxisSuffix),
    }
  }

  // --- Share: pie / donut / funnel / treemap, data[] of {label,value} ---
  if (isShareType(type)) {
    const data = obj.data
    if (!Array.isArray(data) || data.length === 0) return null
    const normalizedData = data
      .map((d) => {
        if (!d || typeof d !== 'object') return null
        const row = d as Record<string, unknown>
        if (typeof row.label !== 'string' || !isFiniteNumber(row.value)) return null
        return {
          label: row.label,
          value: row.value,
          color: optString(row.color),
        }
      })
      .filter(Boolean) as ShareSpec['data']
    if (normalizedData.length === 0) return null
    return {
      type,
      title: optString(obj.title),
      data: normalizedData,
    }
  }

  // --- Scatter: series[] of { points: [{x,y,name?}] } ---
  if (type === 'scatter') {
    const series = obj.series
    if (!Array.isArray(series) || series.length === 0) return null
    const normalizedSeries = series
      .map((s) => {
        if (!s || typeof s !== 'object') return null
        const row = s as Record<string, unknown>
        if (!Array.isArray(row.points) || row.points.length === 0) return null
        const points = row.points
          .map((p) => {
            if (!p || typeof p !== 'object') return null
            const pt = p as Record<string, unknown>
            if (!isFiniteNumber(pt.x) || !isFiniteNumber(pt.y)) return null
            return { x: pt.x, y: pt.y, name: optString(pt.name) }
          })
          .filter(Boolean) as ScatterSpec['series'][number]['points']
        if (points.length === 0) return null
        return { label: optString(row.label), points }
      })
      .filter(Boolean) as ScatterSpec['series']
    if (normalizedSeries.length === 0) return null
    return {
      type: 'scatter',
      title: optString(obj.title),
      xAxisLabel: optString(obj.xAxisLabel),
      yAxisLabel: optString(obj.yAxisLabel),
      series: normalizedSeries,
    }
  }

  // --- Heatmap: xLabels[] + yLabels[] + points[] of [x,y,v] triples ---
  if (type === 'heatmap') {
    const xLabels = obj.xLabels
    const yLabels = obj.yLabels
    const points = obj.points
    if (
      !Array.isArray(xLabels) ||
      xLabels.length === 0 ||
      !xLabels.every((l) => typeof l === 'string') ||
      !Array.isArray(yLabels) ||
      yLabels.length === 0 ||
      !yLabels.every((l) => typeof l === 'string') ||
      !Array.isArray(points) ||
      points.length === 0
    ) {
      return null
    }
    const normalizedPoints = points
      .map((p) => {
        if (!Array.isArray(p) || p.length !== 3 || !p.every(isFiniteNumber)) return null
        return [p[0], p[1], p[2]] as [number, number, number]
      })
      .filter(Boolean) as Array<[number, number, number]>
    if (normalizedPoints.length === 0) return null
    return {
      type: 'heatmap',
      title: optString(obj.title),
      xLabels: xLabels as string[],
      yLabels: yLabels as string[],
      points: normalizedPoints,
      valuePrefix: optString(obj.valuePrefix),
      valueSuffix: optString(obj.valueSuffix),
    }
  }

  return null
}

// Parse a fenced code block's body as a chart spec. We attempt this for ANY
// fenced block (chart / json / no-language) because the model is inconsistent
// about the language tag. Non-chart code never validates as an InlineChartSpec,
// so it harmlessly falls through to normal code rendering.
const chartSpecFromFencedCode = (children: React.ReactNode): InlineChartSpec | null => {
  const raw = String(children).replace(/\n$/, '').trim()
  if (!raw || raw[0] !== '{') return null
  try {
    return toInlineChartSpec(JSON.parse(raw))
  } catch {
    return null
  }
}

// Find the index of the `}` that closes the `{` at `start`, respecting strings
// and escapes. Returns -1 if unbalanced.
const matchJsonObjectEnd = (content: string, start: number): number => {
  let depth = 0
  let inString = false
  let escaped = false
  for (let i = start; i < content.length; i++) {
    const ch = content[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') {
      inString = true
      continue
    }
    if (ch === '{') depth += 1
    else if (ch === '}') {
      depth -= 1
      if (depth === 0) return i
    }
  }
  return -1
}

// The model is supposed to emit charts inside a ```chart fence, but it
// frequently dumps the raw JSON object with no fence at all. Wrap any bare,
// top-level chart-spec JSON in a ```chart fence so the single ReactMarkdown
// pass (and the code component below) renders it as a real chart. Content
// already inside a fenced block is skipped so we never hijack legit code/JSON.
const wrapBareChartJson = (content: string): string => {
  let result = ''
  let i = 0
  while (i < content.length) {
    if (content.startsWith('```', i)) {
      const close = content.indexOf('```', i + 3)
      if (close === -1) {
        result += content.slice(i)
        break
      }
      result += content.slice(i, close + 3)
      i = close + 3
      continue
    }
    if (content[i] === '{') {
      const end = matchJsonObjectEnd(content, i)
      if (end !== -1) {
        const candidate = content.slice(i, end + 1)
        let spec: InlineChartSpec | null = null
        let widgetSpec: ReturnType<typeof toWidgetSpec> = null
        let isValidJson = false
        try {
          const parsed = JSON.parse(candidate)
          isValidJson = true
          spec = toInlineChartSpec(parsed)
          if (!spec) widgetSpec = toWidgetSpec(parsed)
        } catch {
          spec = null
        }
        if (spec) {
          result += `\n\n\`\`\`chart\n${candidate}\n\`\`\`\n\n`
          i = end + 1
          continue
        }
        // Bare dashboard-widget JSON gets its own ```widget fence so the pre
        // handler renders a WidgetPreviewCard instead of a raw JSON dump.
        if (widgetSpec) {
          result += `\n\n\`\`\`widget\n${candidate}\n\`\`\`\n\n`
          i = end + 1
          continue
        }
        // Valid JSON that isn't a chart: skip its whole span (prevents
        // re-scanning/re-parsing nested objects -> O(N^2), and avoids fencing a
        // chart spec nested inside a larger JSON payload).
        if (isValidJson) {
          // If the object stands on its own line(s), fence it as ```json so
          // ReactMarkdown doesn't collapse newlines or treat _/* as emphasis.
          // Inline JSON (mid-sentence) is left verbatim so we don't rip it out
          // of the surrounding prose into a block.
          const prevNonSpace = result.replace(/[ \t]+$/, '').slice(-1)
          let after = end + 1
          while (after < content.length && (content[after] === ' ' || content[after] === '\t')) after += 1
          const nextChar = after >= content.length ? '' : content[after]
          const isBlock =
            (prevNonSpace === '' || prevNonSpace === '\n') &&
            (nextChar === '' || nextChar === '\n')
          if (isBlock) {
            result += `\n\n\`\`\`json\n${candidate}\n\`\`\`\n\n`
          } else {
            result += candidate
          }
          i = end + 1
          continue
        }
      }
    }
    result += content[i]
    i += 1
  }
  return result
}

const InlineChartCardBase: React.FC<{ spec: InlineChartSpec }> = ({ spec }) => {
  const config = useMemo(() => {
    const base = {
      credits: { enabled: false },
      title: { text: spec.title || null },
      legend: { enabled: true },
      // animation:false keeps the chart from replaying its intro every time the
      // component re-renders (e.g. on scroll, when a fresh config is computed).
      chart: { backgroundColor: 'transparent', animation: false, style: { fontFamily: 'Inter, system-ui, sans-serif' } },
    }
    // --- Share family: pie / donut / funnel / treemap (data[]) ---
    if (spec.type === 'pie' || spec.type === 'donut' || spec.type === 'funnel' || spec.type === 'treemap') {
      const points = spec.data.map((d, i) => ({
        name: d.label,
        value: d.value, // treemap reads `value`
        y: d.value, // pie/donut/funnel read `y`
        color: d.color || INLINE_CHART_COLORS[i % INLINE_CHART_COLORS.length],
      }))
      if (spec.type === 'treemap') {
        return {
          ...base,
          chart: { ...base.chart, height: 300 },
          legend: { enabled: false },
          colorAxis: undefined,
          plotOptions: { series: { animation: false } },
          series: [
            {
              type: 'treemap',
              layoutAlgorithm: 'squarified',
              name: spec.title || 'Breakdown',
              data: points,
              dataLabels: { enabled: true, format: '{point.name}' },
            },
          ],
        }
      }
      if (spec.type === 'funnel') {
        return {
          ...base,
          chart: { ...base.chart, height: 300 },
          plotOptions: {
            series: {
              animation: false,
              dataLabels: { enabled: true, format: '<b>{point.name}</b> ({point.y})', softConnector: true },
              center: ['40%', '50%'],
              neckWidth: '30%',
              neckHeight: '25%',
              width: '70%',
            },
          },
          series: [{ type: 'funnel', name: spec.title || 'Funnel', data: points }],
        }
      }
      // pie / donut
      return {
        ...base,
        chart: { ...base.chart, type: 'pie', height: 260 },
        plotOptions: {
          series: { animation: false },
          pie: {
            allowPointSelect: true,
            innerSize: spec.type === 'donut' ? '60%' : undefined,
            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.1f}%' },
          },
        },
        series: [{ type: 'pie', name: spec.title || 'Distribution', data: points }],
      }
    }

    // --- Scatter family: series[] of {x,y} (highcharts-more) ---
    if (spec.type === 'scatter') {
      return {
        ...base,
        chart: { ...base.chart, type: 'scatter', height: 300, zoomType: 'xy' },
        xAxis: { title: { text: spec.xAxisLabel || null } },
        yAxis: { title: { text: spec.yAxisLabel || null } },
        legend: { enabled: spec.series.length > 1 },
        tooltip: {
          // {point.name} renders empty when a point has no name, so this stays
          // clean whether or not the spec provides per-point labels.
          headerFormat: '<b>{series.name}</b><br/>',
          pointFormat: '{point.name}<br/>x: {point.x}, y: {point.y}',
        },
        plotOptions: {
          series: { animation: false },
          scatter: { marker: { radius: 4, symbol: 'circle' } },
        },
        series: spec.series.map((s, i) => ({
          type: 'scatter',
          name: s.label || `Series ${i + 1}`,
          color: INLINE_CHART_COLORS[i % INLINE_CHART_COLORS.length],
          data: s.points.map((p) => ({ x: p.x, y: p.y, name: p.name })),
        })),
      }
    }

    // --- Heatmap family: xLabels/yLabels categories + [x,y,v] points ---
    if (spec.type === 'heatmap') {
      const vp = spec.valuePrefix || ''
      const vs = spec.valueSuffix || ''
      return {
        ...base,
        chart: { ...base.chart, type: 'heatmap', height: 320, marginTop: 40, marginBottom: 80 },
        legend: { enabled: false },
        xAxis: { categories: spec.xLabels, labels: { style: { fontSize: '10px' } } },
        yAxis: { categories: spec.yLabels, title: { text: null }, reversed: true },
        colorAxis: { min: 0, minColor: '#eff6ff', maxColor: '#1d4ed8' },
        tooltip: {
          formatter: function (this: {
            point: { x: number; y: number; value: number }
            series: { xAxis: { categories: string[] }; yAxis: { categories: string[] } }
          }) {
            const xCat = this.series.xAxis.categories[this.point.x]
            const yCat = this.series.yAxis.categories[this.point.y]
            return `<b>${xCat ?? ''}</b> / <b>${yCat ?? ''}</b><br/>${vp}${this.point.value}${vs}`
          },
        },
        plotOptions: { series: { animation: false } },
        series: [
          {
            type: 'heatmap',
            name: spec.title || 'Heatmap',
            borderWidth: 1,
            borderColor: '#ffffff',
            data: spec.points,
            dataLabels: { enabled: spec.points.length <= 100, color: '#1f2937', style: { textOutline: 'none', fontSize: '10px' }, formatter: function (this: { point: { value: number } }) { return `${vp}${this.point.value}${vs}` } },
          },
        ],
      }
    }

    // --- Categorical + combo family: labels[] + datasets[] ---
    const seriesTypeFor = (kind?: 'column' | 'line'): string =>
      kind === 'line' ? 'spline' : kind === 'column' ? 'column' : ''
    const usesRightAxis = spec.type === 'combo' && spec.datasets.some((d) => d.axis === 'right')
    const defaultType =
      spec.type === 'line' || spec.type === 'area' ? 'areaspline' : spec.type === 'combo' ? 'column' : 'column'
    const stacking = spec.type === 'stackedBar' || spec.type === 'area' ? 'normal' : undefined

    const makeAxisLabel = (prefix?: string, suffix?: string) => ({
      formatter: function (this: { value: number }) {
        return `${prefix || ''}${Math.round(this.value)}${suffix || ''}`
      },
    })

    const yAxis: any = usesRightAxis
      ? [
          { title: { text: null }, labels: makeAxisLabel(spec.yAxisPrefix, spec.yAxisSuffix) },
          { title: { text: null }, opposite: true, labels: makeAxisLabel(spec.y2AxisPrefix, spec.y2AxisSuffix) },
        ]
      : { title: { text: null }, labels: makeAxisLabel(spec.yAxisPrefix, spec.yAxisSuffix) }

    return {
      ...base,
      chart: { ...base.chart, type: defaultType, height: 280 },
      xAxis: {
        categories: spec.labels,
        labels: { style: { fontSize: '10px' } },
        tickInterval: Math.max(1, Math.ceil(spec.labels.length / 8)),
      },
      yAxis,
      plotOptions: {
        series: { animation: false, stacking },
        areaspline: { fillOpacity: 0.12, marker: { enabled: spec.labels.length <= 20, radius: 3 }, lineWidth: 2 },
        column: { borderRadius: 3 },
      },
      series: spec.datasets.map((d, i) => {
        const perSeriesType = spec.type === 'combo' ? seriesTypeFor(d.kind) || 'column' : ''
        return {
          ...(perSeriesType ? { type: perSeriesType } : {}),
          name: d.label || `Series ${i + 1}`,
          data: d.data,
          color: INLINE_CHART_COLORS[i % INLINE_CHART_COLORS.length],
          ...(usesRightAxis ? { yAxis: d.axis === 'right' ? 1 : 0 } : {}),
        }
      }),
    }
  }, [spec])

  return (
    <div className="tw-my-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 tw-shadow-sm">
      <HighchartsReact highcharts={Highcharts} options={config} />
    </div>
  )
}

// The `pre` handler parses a fresh spec object on every render, so without this
// memo the chart would rebuild + reflow on every parent re-render (e.g. during
// scroll). Compare specs by serialized content so an unchanged chart keeps a
// stable config reference and Highcharts skips the rebuild.
const InlineChartCard = React.memo(
  InlineChartCardBase,
  (prev, next) => JSON.stringify(prev.spec) === JSON.stringify(next.spec),
)

// Helper to extract plain text from React children (which can be nested elements)
const getTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (!children) return ''

  if (Array.isArray(children)) {
    return children.map(getTextFromChildren).join('')
  }

  // Handle React elements - extract text from their children
  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode; href?: string }
    // For links, include the text content
    if (props.children) {
      return getTextFromChildren(props.children)
    }
    return ''
  }

  // Handle objects that might have a toString
  if (typeof children === 'object') {
    try {
      return String(children)
    } catch {
      return ''
    }
  }

  return ''
}

// Extract the main action title from li children - handles bold text at the start
const extractActionTitle = (children: React.ReactNode): string => {
  const fullText = getTextFromChildren(children)

  // Get first line and clean it up
  let title = fullText.split('\n')[0]
    .replace(/^\*\*|\*\*$/g, '') // Remove markdown bold
    .replace(/^[:\-–—]\s*/, '') // Remove leading colon/dash
    .trim()

  // If title is too long, truncate to first sentence or clause
  if (title.length > 100) {
    const firstSentence = title.match(/^[^.!?:]+[.!?:]?/)
    if (firstSentence) {
      title = firstSentence[0].trim()
    }
  }

  // If still empty, try to find any meaningful text
  if (!title && fullText) {
    // Take first 80 characters of any text we can find
    title = fullText.slice(0, 80).split('\n')[0].trim()
  }

  return title
}

// Section icons mapping
const sectionIcons: Record<string, React.ComponentType<any>> = {
  'executive': FiClipboard,
  'summary': FiClipboard,
  'overview': FiClipboard,
  'key metrics': FiBarChart2,
  'metrics': FiBarChart2,
  'top performers': FiAward,
  'top products': FiPackage,
  'top 10': FiAward,
  'performers': FiAward,
  'analysis': FiTrendingUp,
  'breakdown': FiBarChart2,
  'revenue': FiDollarSign,
  'recommendations': FiTarget,
  'strategic': FiTarget,
  'action': FiTarget,
  'concern': FiAlertTriangle,
  'warning': FiAlertTriangle,
  'issue': FiAlertTriangle,
  'sales': FiDollarSign,
  'traffic': FiTrendingUp,
  'customer': FiUsers,
  'period': FiCalendar,
  'daily': FiCalendar,
  'monthly': FiCalendar,
  'product': FiPackage,
}

// Get icon for section based on title
const getSectionIcon = (title: string): React.ComponentType<any> => {
  const lowerTitle = title.toLowerCase()
  for (const [key, icon] of Object.entries(sectionIcons)) {
    if (lowerTitle.includes(key)) return icon
  }
  return FiBarChart2
}

// Get section color theme
const getSectionTheme = (title: string): { bg: string; text: string; border: string } => {
  const lowerTitle = title.toLowerCase()
  if (lowerTitle.includes('executive') || lowerTitle.includes('summary') || lowerTitle.includes('overview')) {
    return { bg: 'tw-bg-indigo-50', text: 'tw-text-indigo-600', border: 'tw-border-indigo-200' }
  }
  if (lowerTitle.includes('top') || lowerTitle.includes('performer') || lowerTitle.includes('best')) {
    return { bg: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-200' }
  }
  if (lowerTitle.includes('revenue') || lowerTitle.includes('sales') || lowerTitle.includes('profit')) {
    return { bg: 'tw-bg-green-50', text: 'tw-text-green-600', border: 'tw-border-green-200' }
  }
  if (lowerTitle.includes('recommend') || lowerTitle.includes('action') || lowerTitle.includes('strategic')) {
    return { bg: 'tw-bg-violet-50', text: 'tw-text-violet-600', border: 'tw-border-violet-200' }
  }
  if (lowerTitle.includes('concern') || lowerTitle.includes('warning') || lowerTitle.includes('issue') || lowerTitle.includes('problem')) {
    return { bg: 'tw-bg-red-50', text: 'tw-text-red-600', border: 'tw-border-red-200' }
  }
  if (lowerTitle.includes('analysis') || lowerTitle.includes('insight') || lowerTitle.includes('trend')) {
    return { bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-200' }
  }
  return { bg: 'tw-bg-gray-50', text: 'tw-text-gray-600', border: 'tw-border-gray-200' }
}

// Styled section header component
const SectionHeader: React.FC<{ level: 1 | 2 | 3; children: React.ReactNode }> = ({ level, children }) => {
  const text = getTextFromChildren(children)
  const cleanTitle = text.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}#\s]*/u, '').trim()
  const Icon = getSectionIcon(cleanTitle)
  const theme = getSectionTheme(cleanTitle)

  const sizeClasses = {
    1: 'tw-text-xl tw-py-4',
    2: 'tw-text-lg tw-py-3',
    3: 'tw-text-base tw-py-2.5',
  }

  return (
    <div className={`tw-flex tw-items-center tw-gap-3 tw-mt-6 tw-mb-4 first:tw-mt-0 tw-px-4 tw-rounded-lg ${theme.bg} tw-border ${theme.border} ${sizeClasses[level]}`}>
      <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-white tw-shadow-sm ${theme.text}`}>
        <Icon size={level === 1 ? 18 : 16} />
      </div>
      <span className={`tw-font-semibold tw-text-gray-800 ${level === 1 ? 'tw-text-xl' : level === 2 ? 'tw-text-lg' : 'tw-text-base'}`}>
        {cleanTitle}
      </span>
    </div>
  )
}

// Keywords/patterns that indicate an action can be executed via MCP tools
// These map to actual tool capabilities available in the PayHelm MCP server
const ACTIONABLE_PATTERNS = [
  // Google Ads / Tracking
  /tracking\s*(url|template|parameter)/i,
  /utm\s*(parameter|tracking|tag)/i,
  /google\s*ads?\s*(tracking|conversion|campaign|setting)/i,
  /fix\s*(tracking|conversion|utm)/i,
  /repair\s*(tracking|conversion|utm)/i,
  /update\s*(tracking|conversion|utm|setting)/i,
  /enable\s*(tracking|conversion|auto-tagging)/i,
  /set\s*up\s*(tracking|conversion|utm)/i,
  // Campaign management
  /create\s*(campaign|ad|promotion)/i,
  /launch\s*(campaign|ad|promotion)/i,
  /activate\s*(campaign|ad)/i,
  /reactivate\s*(campaign|ad)/i,
  // Data queries (these can be executed to show more data)
  /analyze\s*(data|sales|revenue|traffic|customers)/i,
  /show\s*(data|sales|revenue|traffic|breakdown)/i,
  /get\s*(data|report|analytics)/i,
  /query\s*(data|sales|orders)/i,
  // Settings updates
  /configure\s*(setting|option|parameter)/i,
  /update\s*(setting|configuration)/i,
]

// Check if an action text matches any actionable pattern
const isActionableItem = (actionText: string): boolean => {
  const lowerText = actionText.toLowerCase()
  return ACTIONABLE_PATTERNS.some(pattern => pattern.test(lowerText))
}

// Factory function to create markdown components with action click handler
const createMarkdownComponents = (onActionClick?: (actionNumber: number, actionTitle: string) => void) => ({
  h1: ({ children }: any) => <SectionHeader level={1}>{children}</SectionHeader>,
  h2: ({ children }: any) => <SectionHeader level={2}>{children}</SectionHeader>,
  h3: ({ children }: any) => <SectionHeader level={3}>{children}</SectionHeader>,
  h4: ({ children }: any) => (
    <h4 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mt-4 tw-mb-2">{children}</h4>
  ),
  table: ({ children }: any) => (
    <div className="tw-overflow-x-auto tw-my-4 tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
      <table className="tw-w-full tw-text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="tw-bg-gray-50">{children}</thead>
  ),
  th: ({ children }: any) => (
    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">{children}</th>
  ),
  td: ({ children }: any) => {
    const text = getTextFromChildren(children)
    const isCurrency = /^\$[\d,]+(\.\d{2})?$/.test(text)
    const isNegative = /^-[\d.]+%$/.test(text)
    let cls = "tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-text-gray-700"
    if (isCurrency) cls = "tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-text-green-600 tw-font-semibold"
    if (isNegative) cls = "tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-text-red-600 tw-font-medium"
    return <td className={cls}>{children}</td>
  },
  tr: ({ children }: any) => (
    <tr className="hover:tw-bg-gray-50">{children}</tr>
  ),
  ol: ({ children }: any) => {
    // Count only valid React elements (li items), not whitespace/text nodes
    let liIndex = 0
    return (
      <ol className="tw-my-4 tw-text-gray-700 tw-space-y-4 tw-pl-0 tw-list-none">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            liIndex++
            // Always use sequential numbering starting from 1
            return React.cloneElement(child as React.ReactElement<any>, { orderedIndex: liIndex })
          }
          return child
        })}
      </ol>
    )
  },
  ul: ({ children }: any) => (
    <ul className="tw-list-disc tw-pl-6 tw-space-y-2 tw-my-4 tw-text-gray-700">{children}</ul>
  ),
  li: ({ children, orderedIndex }: any) => {
    // For ordered lists, render with custom number styling
    if (orderedIndex !== undefined) {
      // Extract the action title using our dedicated function
      const fullText = getTextFromChildren(children)
      const mainTitle = extractActionTitle(children)

      // Check if this action has tool support
      const hasToolSupport = isActionableItem(fullText)

      const handleClick = () => {
        if (onActionClick && hasToolSupport && mainTitle) {
          console.log('[ActionClick] Triggering action:', orderedIndex, mainTitle)
          onActionClick(orderedIndex, mainTitle)
        }
      }

      return (
        <li className="tw-text-gray-700 tw-leading-relaxed tw-flex tw-gap-3 tw-group">
          {/* Number badge - only clickable if has tool support */}
          {hasToolSupport && onActionClick ? (
            <button
              type="button"
              onClick={handleClick}
              className="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-bg-violet-100 tw-text-violet-700 tw-font-semibold tw-text-sm tw-flex tw-items-center tw-justify-center hover:tw-bg-violet-200 tw-transition-colors tw-cursor-pointer tw-border-0"
              title={`Execute action #${orderedIndex}: ${mainTitle}`}
            >
              {orderedIndex}
            </button>
          ) : (
            <span className="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 tw-font-semibold tw-text-sm tw-flex tw-items-center tw-justify-center">
              {orderedIndex}
            </span>
          )}
          <div className="tw-flex-1 tw-pt-0.5">
            {children}
            {/* Only show "Do this" button if the action has tool support */}
            {hasToolSupport && onActionClick && (
              <button
                type="button"
                onClick={handleClick}
                className="tw-ml-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-violet-600 hover:tw-text-violet-800 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-bg-violet-50 hover:tw-bg-violet-100 tw-px-2 tw-py-1 tw-rounded-full tw-border-0 tw-cursor-pointer"
              >
                <FiPlay size={10} />
                Do this
              </button>
            )}
          </div>
        </li>
      )
    }
    // For unordered lists, keep simple
    return <li className="tw-text-gray-700 tw-leading-relaxed">{children}</li>
  },
  strong: ({ children }: any) => (
    <strong className="tw-font-semibold tw-text-gray-900">{children}</strong>
  ),
  em: ({ children }: any) => (
    <em className="tw-italic tw-text-gray-600">{children}</em>
  ),
  p: ({ children }: any) => (
    <p className="tw-text-gray-700 tw-leading-relaxed tw-my-3">{children}</p>
  ),
  hr: () => <hr className="tw-my-6 tw-border-gray-200" />,
  a: ({ href, children }: any) => {
    // Integration connect CTAs: /integrations (SPA) or /auth (API OAuth start)
    if (href && (href.startsWith('/integrations') || href.startsWith('/auth'))) {
      const text = getTextFromChildren(children)
      const resolvedHref = href.startsWith('/auth')
        ? `${getApiHost(window.location.hostname).replace(/\/+$/, '')}${href}`
        : href
      return (
        <a
          href={resolvedHref}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-my-2 tw-rounded-lg tw-bg-gradient-to-r tw-from-blue-600 tw-to-indigo-600 tw-text-white tw-text-sm tw-font-semibold tw-no-underline hover:tw-from-blue-700 hover:tw-to-indigo-700 tw-shadow-sm hover:tw-shadow-md tw-transition-all"
        >
          <FiLink2 size={15} />
          {text}
        </a>
      )
    }
    // Standalone popup pages (currently just the eBay import popup) — open as
    // a sized window via window.open() so the user gets a focused dialog
    // instead of a full-width tab. Browsers honor window.features only when
    // called from a real user gesture, so the onClick wrapper matters.
    if (href && /\/import-orders\/ebay-popup(?:[?#]|$)/.test(href)) {
      const text = getTextFromChildren(children)
      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        const w = 720
        const h = 800
        const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2))
        const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2))
        window.open(href, 'payhelm-ebay-import', `popup=1,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`)
      }
      return (
        <a
          href={href}
          onClick={handleClick}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-my-2 tw-rounded-lg tw-bg-gradient-to-r tw-from-blue-600 tw-to-indigo-600 tw-text-white tw-text-sm tw-font-semibold tw-no-underline hover:tw-from-blue-700 hover:tw-to-indigo-700 tw-shadow-sm hover:tw-shadow-md tw-transition-all"
        >
          <FiLink2 size={15} />
          {text}
        </a>
      )
    }
    return (
      <a href={href} className="tw-text-blue-600 hover:tw-underline" target="_blank" rel="noopener noreferrer">{children}</a>
    )
  },
  blockquote: ({ children }: any) => (
    <blockquote className="tw-border-l-4 tw-border-blue-400 tw-bg-blue-50 tw-pl-4 tw-py-3 tw-my-4 tw-rounded-r-lg tw-text-gray-700">
      {children}
    </blockquote>
  ),
  // react-markdown wraps fenced code in <pre><code>. Detect chart specs here
  // (the <code> child carries the fence body in its props) so a real chart
  // renders instead of the dark code box.
  pre: ({ children }: any) => {
    const only = React.Children.toArray(children)[0]
    if (React.isValidElement(only)) {
      const codeProps = (only.props as any) || {}
      // Dashboard-widget fences (tagged ```widget, or bare widget JSON we
      // wrapped upstream) render as an interactive preview card. An invalid
      // widget block must never fall through to a raw JSON dump.
      if (/language-widget/.test(codeProps.className || '')) {
        const widgetSpec = widgetSpecFromFencedCode(String(codeProps.children).replace(/\n$/, ''))
        if (widgetSpec) return <WidgetPreviewCard spec={widgetSpec} />
        return (
          <div className="tw-my-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4 tw-text-sm tw-text-gray-500">
            Widget unavailable
          </div>
        )
      }
      const spec = chartSpecFromFencedCode(codeProps.children)
      if (spec) return <InlineChartCard spec={spec} />
      // A block the model meant as a chart (tagged ```chart, or bare chart
      // JSON we wrapped upstream) must never fall through to a raw JSON dump.
      if (/language-chart/.test(codeProps.className || '')) {
        return (
          <div className="tw-my-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4 tw-text-sm tw-text-gray-500">
            Chart unavailable
          </div>
        )
      }
      // The model is as inconsistent about the ```widget tag as it is about
      // ```chart — try any remaining fenced JSON as a widget spec before
      // falling back to a code box. Non-widget JSON never validates.
      const widgetSpec = widgetSpecFromFencedCode(String(codeProps.children).replace(/\n$/, ''))
      if (widgetSpec) return <WidgetPreviewCard spec={widgetSpec} />
    }
    return (
      <pre className="tw-bg-gray-900 tw-text-gray-100 tw-p-4 tw-rounded-lg tw-overflow-x-auto tw-my-4">
        {children}
      </pre>
    )
  },
  code: ({ inline, className, children }: any) => {
    if (inline) {
      return <code className="tw-px-1.5 tw-py-0.5 tw-bg-gray-100 tw-rounded tw-text-sm tw-font-mono">{children}</code>
    }
    return <code className={`tw-text-sm tw-font-mono ${className || ''}`}>{children}</code>
  },
})

const AgentResponseRenderer: React.FC<AgentResponseRendererProps> = ({ content, onActionClick, bare = false }) => {
  // Memoize the markdown components to avoid re-creating on every render
  const markdownComponents = useMemo(() => createMarkdownComponents(onActionClick), [onActionClick])

  if (!content) return null

  // Clean up any escaped markdown characters and ensure proper line breaks
  const cleanContent = wrapBareChartJson(
    stripLeadingPlanBlock(content)
      .replace(/\\([#*_`~\[\]])/g, '$1')  // Unescape markdown chars
      .replace(/\r\n/g, '\n')              // Normalize line endings
      .replace(/\r/g, '\n')
      .replace(/\n{3,}/g, '\n\n')          // Collapse excess newlines
      .trim()
  )

  const rendered = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {cleanContent}
    </ReactMarkdown>
  )

  if (bare) {
    return <div className="tw-agent-response">{rendered}</div>
  }

  return (
    <div className="tw-agent-response tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-sm tw-border tw-border-gray-100">
      {rendered}
    </div>
  )
}

export default AgentResponseRenderer
